import type { BranchKey, WorkspaceKey } from '@festina-life/api'
import { ErrorMessage } from '@festina-life/components'
import { Alert } from '@festina-life/ui/feedback'
import { RawRadioGroup } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { SimpleModal } from '@festina-life/ui/structure'
import cx from 'classnames'
import { Suspense, useId, useMemo, useState } from 'react'

import type { WorkspaceCreateOrUpdateInput } from '../../uiModels/workspace/types'
import { isEqualWorkspace } from '../../workspace/helpers'
import { useCreateWorkspace } from '../workspaceCreationModal/useCreateWorkspace'
import { WorkspaceForm } from '../workspaceCreationModal/WorkspaceForm'
import { WorkspaceName } from '../workspaceName/WorkspaceName'

type Props = {
  options: WorkspaceKey[]
  onSelected: (workspace: WorkspaceKey) => void
  onCancel: () => void
  baseBranch: BranchKey
}

const SelectionOptions = ['existing', 'new'] as const
type SelectionOption = (typeof SelectionOptions)[number]

export const WorkspaceSelectorOrCreator = ({
  options,
  onSelected,
  baseBranch,
  onCancel,
}: Props) => {
  const [selectedKey, setSelectedKey] = useState<WorkspaceKey>()
  const [selectionMode, setSelectionMode] =
    useState<SelectionOption>('existing')
  const formId = useId()
  const translate = useTranslate()
  const { mutateAsync, isPending, error } = useCreateWorkspace(baseBranch)
  const SelectionOptionTranslations: Record<SelectionOption, string> = useMemo(
    () => ({
      new: translate('workspaceSelectorOrCreator.option.new'),
      existing: translate('workspaceSelectorOrCreator.option.existing'),
    }),
    [translate],
  )

  const handleSubmit = (values: WorkspaceCreateOrUpdateInput) =>
    mutateAsync(values).then(onSelected)

  const handleSelectExistingWorkspace = () => {
    if (selectedKey) {
      onSelected(selectedKey)
    }
  }

  return (
    <SimpleModal
      isOpen
      title={translate('workspaceSelectorOrCreator.title')}
      closeConfiguration={{ label: translate('cancel'), onClose: onCancel }}
      acceptConfiguration={
        selectionMode === 'new'
          ? {
              formId,
              loading: isPending,
            }
          : {
              formId,
              onAccept: handleSelectExistingWorkspace,
            }
      }
      bodyOuterClassName="w-md max-h-1/2"
    >
      <p>{translate('workspaceSelectorOrCreator.description')}</p>
      <RawRadioGroup
        options={SelectionOptions}
        onChange={setSelectionMode}
        renderFn={(option) => SelectionOptionTranslations[option]}
        value={selectionMode}
      />
      <div className="flex flex-col gap-y-2">
        {selectionMode === 'new' ? (
          <>
            <Alert
              severity="info"
              title={translate(
                'workspaceSelectorOrCreator.option.create.description',
                {
                  branchName: baseBranch.name,
                },
              )}
            />
            <ErrorMessage error={error} />
            <Suspense>
              <WorkspaceForm
                formId={formId}
                initialValues={{
                  name: '',
                  branchKey: baseBranch,
                }}
                onSubmit={handleSubmit}
              />
            </Suspense>
          </>
        ) : (
          <>
            <Alert
              severity="info"
              title={translate(
                'workspaceSelectorOrCreator.option.existing.description',
                {
                  branchName: baseBranch.name,
                },
              )}
            />
            <ul className="flex flex-col gap-y-1">
              {options.map((option) => (
                <li
                  key={option.name}
                  className={cx(
                    'border-gray-300 dark:border-gray-400 border cursor-pointer rounded-md p-1',
                    isEqualWorkspace(option, selectedKey)
                      ? 'bg-primary-200 dark:bg-primary-700'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-600',
                  )}
                  onClick={() => setSelectedKey(option)}
                >
                  <WorkspaceName isShowingIcon={false} workspaceKey={option} />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </SimpleModal>
  )
}
